// Importar planilha, Listas (tipos/sistemas/status) e Consultar protocolo.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton,
  List, ListItem, ListItemText, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
  makeStyles,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import BlockIcon from "@material-ui/icons/Block";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import FileCopyOutlined from "@material-ui/icons/FileCopyOutlined";
import ShareIcon from "@material-ui/icons/Share";

import ConfirmationModal from "../../components/ConfirmationModal";
import MainContainer from "../../components/MainContainer";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import suporteApi, { mensagemErro, urlConsultaPublica, urlPublica } from "../api";
import { DialogCard, DialogProtocolo, SITUACAO, carregarListas, copiar, dataBR, duracao } from "../comum";

// Listas: cada painel rola sozinho dentro da altura da tela (a lista de itens pode ser longa)
const ALTURA_PAINEL = "calc(100vh - 150px)";

const useStyles = makeStyles((theme) => ({
  papel: { padding: 16, overflowY: "auto", flex: 1, ...theme.scrollbarStyles },
  aviso: { padding: "8px 12px", borderRadius: 6, margin: "12px 0", fontWeight: 600, background: theme.palette.action.hover },
  inativo: { opacity: 0.5, textDecoration: "line-through" },
  codigo: { fontFamily: "monospace", fontSize: 18, fontWeight: 700, wordBreak: "break-all" },
  mono: { fontFamily: "monospace" },
}));

// ------------------------------------------------------------------ importar planilha
const resumo = (r) =>
  [
    r.tecnicos && `Técnicos: ${r.tecnicos.novos} novos, ${r.tecnicos.atualizados} atualizados`,
    r.clientes && `Clientes: ${r.clientes.novos} novos, ${r.clientes.atualizados} atualizados`,
    r.chamados && `Chamados: ${r.chamados.novos_concluidos + r.chamados.novos_pendentes} novos`,
    r.clientes_dos_chamados && `Contas criadas: ${r.clientes_dos_chamados.clientes_criados}`,
  ].filter(Boolean).join(" · ");

export const Importar = () => {
  const c = useStyles();
  const [arquivo, setArquivo] = useState(null);
  const [op, setOp] = useState({ tecnicos: true, clientes: true, chamados: true, clientes_faltantes: false, simular: true });
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [historico, setHistorico] = useState([]);

  const carregarHistorico = useCallback(
    () => suporteApi.get("/api/importacoes").then((r) => setHistorico(r.data)).catch(() => {}),
    []
  );
  useEffect(() => {
    carregarHistorico();
  }, [carregarHistorico]);

  const enviar = async () => {
    const fd = new FormData();
    fd.append("arquivo", arquivo);
    Object.entries(op).forEach(([k, v]) => fd.append(k, v));
    setEnviando(true);
    setResultado(null);
    try {
      const { data } = await suporteApi.post("/api/importar", fd);
      setResultado(data);
      if (!data.simulacao) carregarListas(true);
      carregarHistorico();
    } catch (err) {
      toast.error(mensagemErro(err));
    }
    setEnviando(false);
  };
  const marca = (nome, rotulo) => (
    <FormControlLabel
      control={<Checkbox color="primary" checked={op[nome]} onChange={(e) => setOp({ ...op, [nome]: e.target.checked })} />}
      label={rotulo}
    />
  );
  const tabela = (titulo, linhas) => (
    <Grid item xs={12} sm={6} md={3}>
      <Table size="small">
        <TableHead><TableRow><TableCell colSpan={2}><b>{titulo}</b></TableCell></TableRow></TableHead>
        <TableBody>
          {linhas.map(([r, v]) => (
            <TableRow key={r}><TableCell>{r}</TableCell><TableCell align="right">{(v ?? 0).toLocaleString("pt-BR")}</TableCell></TableRow>
          ))}
        </TableBody>
      </Table>
    </Grid>
  );

  return (
    <MainContainer>
      <MainHeader><Title>Importar planilha</Title></MainHeader>
      <Paper className={c.papel} variant="outlined">
        <Typography variant="body2" color="textSecondary">
          Envie o .xlsx baixado do Google Sheets (Arquivo › Fazer download › Microsoft Excel). O sistema lê as abas
          BASE|TÉCNICO, BASE | CLIENTES e ATENDIMENTO | SUPORTE. Pode repetir: cadastros são atualizados e chamados
          só entram se ainda não existirem. Nada é apagado.
        </Typography>
        <div style={{ margin: "12px 0" }}>
          <input type="file" accept=".xlsx,.xlsm" onChange={(e) => setArquivo(e.target.files[0] || null)} />
        </div>
        <div>{marca("tecnicos", "Base técnico")}{marca("clientes", "Base clientes")}{marca("chamados", "Chamados (só novos)")}</div>
        <div>{marca("clientes_faltantes", "Cadastrar como cliente as contas dos chamados que não estão na base")}</div>
        <div>{marca("simular", "Só simular (mostra o que vai mudar, sem gravar)")}</div>
        <Button variant="contained" color="primary" disabled={!arquivo || enviando} onClick={enviar}>
          {enviando ? "Processando… (até 1 minuto)" : "Enviar"}
        </Button>
        {resultado && (
          <>
            <div className={c.aviso}>
              {resultado.simulacao ? "SIMULAÇÃO — nada foi gravado. Desmarque “Só simular” para gravar." : "Importação gravada."}
            </div>
            <Grid container spacing={2}>
              {resultado.tecnicos && tabela("Base técnico", [["Lidos", resultado.tecnicos.lidos], ["Novos", resultado.tecnicos.novos], ["Atualizados", resultado.tecnicos.atualizados], ["Sem mudança", resultado.tecnicos.iguais]])}
              {resultado.clientes && tabela("Base clientes", [["Lidos", resultado.clientes.lidos], ["Novos", resultado.clientes.novos], ["Atualizados", resultado.clientes.atualizados], ["Sem mudança", resultado.clientes.iguais]])}
              {resultado.chamados && tabela("Chamados", [["Lidos", resultado.chamados.lidos], ["Novos concluídos", resultado.chamados.novos_concluidos], ["Novos pendentes", resultado.chamados.novos_pendentes], ["Já existiam", resultado.chamados.ja_existiam]])}
              {resultado.clientes_dos_chamados && tabela("Contas dos chamados", [["Clientes criados", resultado.clientes_dos_chamados.clientes_criados], ["Chamados vinculados", resultado.clientes_dos_chamados.chamados_vinculados]])}
            </Grid>
            {[...(resultado.tecnicos?.conflitos || []), ...(resultado.avisos || [])].map((a) => (
              <Typography key={a} color="error" variant="body2">{a}</Typography>
            ))}
          </>
        )}
        <Typography variant="subtitle2" style={{ marginTop: 24 }}>Últimas importações</Typography>
        <Table size="small">
          <TableHead>
            <TableRow><TableCell>Quando</TableCell><TableCell>Usuário</TableCell><TableCell>Arquivo</TableCell><TableCell>Tipo</TableCell><TableCell>Resumo</TableCell></TableRow>
          </TableHead>
          <TableBody>
            {historico.map((i) => (
              <TableRow key={i.id}>
                <TableCell>{dataBR(i.quando)}</TableCell><TableCell>{i.usuario}</TableCell>
                <TableCell>{i.arquivo}</TableCell><TableCell>{i.simulacao ? "Simulação" : <b>Gravada</b>}</TableCell>
                <TableCell>{resumo(i.resultado || {})}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

// ------------------------------------------------------------------ listas
// À esquerda as listas (com filtro por fila); à direita a lista escolhida: dados, filas e itens.
const ItensLista = ({ lista, onMudou }) => {
  const c = useStyles();
  const [itens, setItens] = useState([]);
  const [novo, setNovo] = useState("");
  const [busca, setBusca] = useState("");
  const [editando, setEditando] = useState(null);
  const [excluindo, setExcluindo] = useState(null);
  const cat = lista.categoria;

  const carregar = useCallback(async () => {
    try {
      setItens((await suporteApi.get(`/api/listas/${cat}`)).data);
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  }, [cat]);
  useEffect(() => {
    setEditando(null);
    setBusca("");
    carregar();
  }, [carregar]);
  const depois = () => { carregar(); onMudou(); carregarListas(true); };

  const incluir = async () => {
    if (!novo.trim()) return;
    try {
      await suporteApi.post(`/api/listas/${cat}`, { nome: novo });
      setNovo("");
      depois();
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  };
  const alternar = async (it) => {
    try {
      await suporteApi.put(`/api/listas/${cat}/${it.codigo}`, { ativo: !it.ativo });
      depois();
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  };
  const salvarNome = async () => {
    try {
      const { data } = await suporteApi.patch(`/api/listas/${cat}/${editando.codigo}`, { nome: editando.nome });
      if (data.atendimentos) toast.success(`Nome corrigido também em ${data.atendimentos} atendimento(s)`);
      setEditando(null);
      depois();
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  };
  const excluir = async () => {
    try {
      await suporteApi.delete(`/api/listas/${cat}/${excluindo.codigo}`);
      toast.success("Item excluído");
      depois();
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  };

  const f = busca.trim().toUpperCase();
  const visiveis = itens.filter((it) => !f || it.nome.includes(f) || String(it.codigo) === f);
  return (
    <>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", margin: "8px 0" }}>
        <TextField size="small" variant="outlined" placeholder="Novo item" value={novo} style={{ flex: "1 1 220px" }}
          onChange={(e) => setNovo(e.target.value)} onKeyDown={(e) => e.key === "Enter" && incluir()} />
        <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={incluir} disabled={!novo.trim()}>Incluir</Button>
        {itens.length > 8 && (
          <TextField size="small" placeholder="Filtrar itens…" value={busca} onChange={(e) => setBusca(e.target.value)} style={{ width: 180 }} />
        )}
      </div>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 70 }}>Código</TableCell><TableCell>Item</TableCell>
              <TableCell style={{ width: 110 }}>Situação</TableCell><TableCell align="right" style={{ width: 130 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {!visiveis.length && (
              <TableRow><TableCell colSpan={4}><Typography variant="body2" color="textSecondary">
                {itens.length ? "Nenhum item com esse filtro." : "Lista vazia. Inclua o primeiro item acima."}
              </Typography></TableCell></TableRow>
            )}
            {visiveis.map((it) => (
              <TableRow key={it.codigo} hover className={it.ativo ? "" : c.inativo}>
                <TableCell className={c.mono}>{it.codigo}</TableCell>
                <TableCell>
                  {editando?.codigo === it.codigo ? (
                    <TextField size="small" fullWidth autoFocus value={editando.nome}
                      onChange={(e) => setEditando({ ...editando, nome: e.target.value })}
                      onKeyDown={(e) => { if (e.key === "Enter") salvarNome(); if (e.key === "Escape") setEditando(null); }} />
                  ) : it.nome}
                </TableCell>
                <TableCell>{it.ativo ? "Ativo" : "Desativado"}</TableCell>
                <TableCell align="right" style={{ whiteSpace: "nowrap" }}>
                  {editando?.codigo === it.codigo ? (
                    <>
                      <IconButton size="small" title="Salvar" onClick={salvarNome}><CheckIcon fontSize="small" /></IconButton>
                      <IconButton size="small" title="Cancelar" onClick={() => setEditando(null)}><CloseIcon fontSize="small" /></IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton size="small" title="Renomear" onClick={() => setEditando({ codigo: it.codigo, nome: it.nome })}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" title={it.ativo ? "Desativar" : "Reativar"} onClick={() => alternar(it)}>
                        {it.ativo ? <BlockIcon fontSize="small" /> : <CheckIcon fontSize="small" />}
                      </IconButton>
                      <IconButton size="small" title="Excluir" onClick={() => setExcluindo(it)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
      <ConfirmationModal title={excluindo ? `Excluir “${excluindo.nome}”?` : ""} open={!!excluindo}
        onClose={() => setExcluindo(null)} onConfirm={excluir}>
        {lista.sistema
          ? "O item sai da lista de vez. Se algum atendimento já usou, a exclusão é recusada (desative em vez de excluir)."
          : "O item sai da lista de vez."}
      </ConfirmationModal>
    </>
  );
};

const DadosLista = ({ lista, filas, onSalvo, onExcluida }) => {
  const [f, setF] = useState(lista);
  const [confirmar, setConfirmar] = useState(false);
  useEffect(() => setF(lista), [lista]);
  const mudou = f.nome !== lista.nome || (f.descricao || "") !== (lista.descricao || "") || f.ativo !== lista.ativo
    || JSON.stringify([...(f.filas || [])].sort()) !== JSON.stringify([...(lista.filas || [])].sort());
  const salvar = async () => {
    try {
      const { data } = await suporteApi.put(`/api/listas-config/${lista.categoria}`,
        { nome: f.nome, descricao: f.descricao || "", filas: f.filas || [], ativo: f.ativo });
      toast.success("Lista salva");
      onSalvo(data);
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  };
  const excluir = async () => {
    try {
      await suporteApi.delete(`/api/listas-config/${lista.categoria}`);
      toast.success("Lista excluída");
      onExcluida();
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  };
  return (
    <Paper variant="outlined" style={{ padding: 14 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" variant="outlined" label="Nome da lista" value={f.nome}
            onChange={(e) => setF({ ...f, nome: e.target.value })} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth size="small" variant="outlined" label="Descrição (opcional)" value={f.descricao || ""}
            onChange={(e) => setF({ ...f, descricao: e.target.value })} />
        </Grid>
        <Grid item xs={12}>
          <Typography variant="subtitle2">Filas que usam esta lista</Typography>
          <Typography variant="caption" color="textSecondary" component="div">Sem nenhuma marcada, vale para todas as filas.</Typography>
          {filas.map((q) => (
            <FormControlLabel key={q.id} label={q.nome} control={
              <Checkbox color="primary" size="small" checked={(f.filas || []).includes(q.id)}
                onChange={(e) => setF({ ...f, filas: e.target.checked ? [...(f.filas || []), q.id] : (f.filas || []).filter((x) => x !== q.id) })} />
            } />
          ))}
          <FormControlLabel label="Lista ativa" control={
            <Checkbox color="primary" size="small" checked={f.ativo !== false} onChange={(e) => setF({ ...f, ativo: e.target.checked })} />
          } />
        </Grid>
      </Grid>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <Button variant="contained" color="primary" disabled={!mudou || f.nome.trim().length < 2} onClick={salvar}>Salvar lista</Button>
        {mudou && <Button onClick={() => setF(lista)}>Desfazer</Button>}
        <div style={{ flex: 1 }} />
        {lista.sistema
          ? <Typography variant="caption" color="textSecondary">Lista do sistema: usada no card e no protocolo. Os códigos nunca mudam.</Typography>
          : <Button startIcon={<DeleteOutlineIcon />} onClick={() => setConfirmar(true)}>Excluir lista</Button>}
      </div>
      <ConfirmationModal title={`Excluir a lista “${lista.nome}”?`} open={confirmar} onClose={() => setConfirmar(false)} onConfirm={excluir}>
        A lista e todos os itens dela serão apagados.
      </ConfirmationModal>
    </Paper>
  );
};

export const Listas = () => {
  const c = useStyles();
  const [listas, setListas] = useState([]);
  const [filas, setFilas] = useState([]);
  const [filtroFila, setFiltroFila] = useState("");
  const [sel, setSel] = useState(null);
  const [criando, setCriando] = useState(false);
  const [nova, setNova] = useState({ nome: "", descricao: "", filas: [] });

  const carregar = useCallback(async (escolher) => {
    try {
      const { data } = await suporteApi.get("/api/listas-config");
      setListas(data);
      setSel((s) => escolher || s || data[0]?.categoria || null);
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  }, []);
  useEffect(() => {
    carregar();
    suporteApi.get("/api/formularios/filas").then((r) => setFilas(r.data)).catch(() => {});
  }, [carregar]);

  const nomeFila = (id) => (filas.find((q) => q.id === id) || {}).nome || `fila ${id}`;
  const visiveis = listas.filter((l) => !filtroFila || !(l.filas || []).length || l.filas.includes(Number(filtroFila)));
  const atual = listas.find((l) => l.categoria === sel);

  const criar = async () => {
    try {
      const { data } = await suporteApi.post("/api/listas-config", { ...nova, ativo: true });
      setCriando(false);
      setNova({ nome: "", descricao: "", filas: filtroFila ? [Number(filtroFila)] : [] });
      toast.success("Lista criada. Agora inclua os itens.");
      carregar(data.categoria);
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>Listas</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="contained" color="primary" startIcon={<AddIcon />}
            onClick={() => { setNova({ nome: "", descricao: "", filas: filtroFila ? [Number(filtroFila)] : [] }); setCriando(true); }}>
            Nova lista
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Grid container spacing={2} style={{ flex: 1, minHeight: 0 }}>
        <Grid item xs={12} md={4} style={{ display: "flex", flexDirection: "column" }}>
          <Paper variant="outlined" className={c.papel} style={{ padding: 0, maxHeight: ALTURA_PAINEL }}>
            <div style={{ padding: "10px 12px", display: "flex", gap: 6, flexWrap: "wrap" }}>
              <Chip size="small" clickable label="Todas as filas" color={!filtroFila ? "primary" : "default"} onClick={() => setFiltroFila("")} />
              {filas.map((q) => (
                <Chip key={q.id} size="small" clickable label={q.nome} color={String(q.id) === String(filtroFila) ? "primary" : "default"}
                  onClick={() => setFiltroFila(String(q.id))} />
              ))}
            </div>
            <List disablePadding>
              {visiveis.map((l) => (
                <ListItem key={l.categoria} button selected={l.categoria === sel} onClick={() => setSel(l.categoria)}
                  style={{ borderTop: "1px solid rgba(0,0,0,0.08)", opacity: l.ativo ? 1 : 0.55 }}>
                  <ListItemText
                    primary={<span style={{ fontWeight: 600 }}>{l.nome}</span>}
                    secondary={
                      <span style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center", marginTop: 2 }}>
                        <span>{`${l.ativos} ativo(s)${l.itens > l.ativos ? ` · ${l.itens - l.ativos} desativado(s)` : ""}`}</span>
                        {(l.filas || []).length
                          ? l.filas.map((id) => <Chip key={id} size="small" variant="outlined" color="primary" label={nomeFila(id)} style={{ height: 18, fontSize: 10.5 }} />)
                          : <Chip size="small" variant="outlined" label="Todas as filas" style={{ height: 18, fontSize: 10.5 }} />}
                        {l.sistema && <Chip size="small" label="Sistema" style={{ height: 18, fontSize: 10.5 }} />}
                      </span>
                    }
                  />
                </ListItem>
              ))}
              {!visiveis.length && <Typography color="textSecondary" style={{ padding: 16 }}>Nenhuma lista nesta fila.</Typography>}
            </List>
          </Paper>
        </Grid>
        <Grid item xs={12} md={8} style={{ display: "flex", flexDirection: "column" }}>
          <Paper variant="outlined" className={c.papel} style={{ maxHeight: ALTURA_PAINEL }}>
            {atual ? (
              <>
                <Typography variant="h6" gutterBottom>{atual.nome}</Typography>
                <DadosLista lista={atual} filas={filas}
                  onSalvo={(l) => setListas((ls) => ls.map((x) => (x.categoria === l.categoria ? { ...x, ...l } : x)))}
                  onExcluida={() => { setSel(null); carregar(); }} />
                <Typography variant="subtitle1" style={{ marginTop: 16 }}>Itens</Typography>
                {atual.sistema && (
                  <Typography variant="caption" color="textSecondary" component="div">
                    Renomear mantém o código e corrige o nome nos atendimentos. Item já usado em atendimento só pode ser desativado.
                  </Typography>
                )}
                <ItensLista lista={atual} onMudou={() => carregar()} />
              </>
            ) : <Typography color="textSecondary">Escolha uma lista à esquerda ou crie uma nova.</Typography>}
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={criando} onClose={() => setCriando(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nova lista</DialogTitle>
        <DialogContent>
          <TextField fullWidth autoFocus margin="dense" variant="outlined" label="Nome" placeholder="Ex.: Status do candidato"
            value={nova.nome} onChange={(e) => setNova({ ...nova, nome: e.target.value })} />
          <TextField fullWidth margin="dense" variant="outlined" label="Descrição (opcional)"
            value={nova.descricao} onChange={(e) => setNova({ ...nova, descricao: e.target.value })} />
          <Typography variant="subtitle2" style={{ marginTop: 8 }}>Filas</Typography>
          {filas.map((q) => (
            <FormControlLabel key={q.id} label={q.nome} control={
              <Checkbox color="primary" size="small" checked={nova.filas.includes(q.id)}
                onChange={(e) => setNova({ ...nova, filas: e.target.checked ? [...nova.filas, q.id] : nova.filas.filter((x) => x !== q.id) })} />
            } />
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCriando(false)}>Cancelar</Button>
          <Button color="primary" variant="contained" disabled={nova.nome.trim().length < 2} onClick={criar}>Criar</Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

// ------------------------------------------------------------------ consultar protocolo
export const ConsultaProtocolo = () => {
  const c = useStyles();
  const [codigo, setCodigo] = useState("");
  const [res, setRes] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [aberto, setAberto] = useState(null);
  const [resposta, setResposta] = useState(null);
  const [recentes, setRecentes] = useState(() => {
    try { return JSON.parse(localStorage.getItem("suporte:protocolos") || "[]"); } catch { return []; }
  });

  const consultar = async (valor) => {
    const cod = (valor ?? codigo).trim();
    if (!cod) return;
    setCodigo(cod);
    setRes(null);
    setBuscando(true);
    try {
      const { data } = await suporteApi.get(`/api/protocolo/${encodeURIComponent(cod)}`);
      setRes(data);
      const novos = [data.protocolo, ...recentes.filter((x) => x !== data.protocolo)].slice(0, 6);
      setRecentes(novos);
      try { localStorage.setItem("suporte:protocolos", JSON.stringify(novos)); } catch { /* sem armazenamento */ }
    } catch (err) {
      toast.error(mensagemErro(err));
    }
    setBuscando(false);
  };
  const d = res?.decodificado;
  const at = res?.atendimentos?.[0];
  // página pública de consulta (sem login), para outras equipes
  const compartilharPagina = async () => {
    const url = `${urlPublica}/consulta`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "Consulta de protocolo", text: "Consulte protocolos de atendimento do suporte", url });
        return;
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
    }
    copiar(url);
  };
  // página pública do protocolo (a mesma do QR code / link enviado ao técnico)
  const compartilhar = async () => {
    const url = urlConsultaPublica(res.protocolo);
    try {
      if (navigator.share) {
        await navigator.share({ title: `Protocolo ${res.protocolo}`, text: `Consulta do protocolo ${res.protocolo}`, url });
        return;
      }
    } catch (err) {
      if (err?.name === "AbortError") return;
    }
    copiar(url);
  };
  const Campo = ({ rotulo, children }) => (children ? (
    <div style={{ display: "flex", gap: 8, padding: "3px 0", fontSize: 14 }}>
      <span style={{ minWidth: 110, opacity: 0.65 }}>{rotulo}</span><span>{children}</span>
    </div>
  ) : null);
  const Bloco = ({ titulo, children }) => (
    <Grid item xs={12} sm={6}>
      <Paper variant="outlined" style={{ padding: 12, height: "100%" }}>
        <Typography variant="subtitle2" gutterBottom>{titulo}</Typography>
        {children}
      </Paper>
    </Grid>
  );

  return (
    <MainContainer>
      <MainHeader>
        <Title>Consultar protocolo</Title>
        <MainHeaderButtonsWrapper>
          <Button startIcon={<ShareIcon />} onClick={compartilharPagina}>Compartilhar página de consulta</Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper className={c.papel} variant="outlined">
        <form onSubmit={(e) => { e.preventDefault(); consultar(); }} style={{ display: "flex", gap: 10 }}>
          <TextField fullWidth variant="outlined" autoFocus value={codigo} onChange={(e) => setCodigo(e.target.value.toUpperCase())}
            placeholder="Cole o protocolo"
            inputProps={{ style: { fontFamily: "monospace", fontSize: 16 } }} />
          <Button type="submit" variant="contained" color="primary" disabled={buscando}>Consultar</Button>
        </form>
        {recentes.length > 0 && !res && (
          <div style={{ marginTop: 10, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            <Typography variant="caption" color="textSecondary">Consultados recentemente:</Typography>
            {recentes.map((r) => <Chip key={r} size="small" label={r} clickable onClick={() => consultar(r)} style={{ fontFamily: "monospace" }} />)}
          </div>
        )}

        {res && !res.legado && (
          <>
            <Paper variant="outlined" style={{ padding: 14, marginTop: 16, borderLeft: "5px solid #2e7d32" }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <Chip size="small" label="✔ Protocolo válido" style={{ background: "#2e7d32", color: "#fff" }} />
                {at && <Chip size="small" variant="outlined" label={SITUACAO[at.status] || at.status} />}
                <div style={{ flex: 1 }} />
                <Button size="small" startIcon={<FileCopyOutlined />} onClick={() => copiar(res.protocolo)}>Copiar</Button>
                <Button size="small" startIcon={<ShareIcon />} onClick={compartilhar}>Compartilhar</Button>
                <Button size="small" href={urlConsultaPublica(res.protocolo)} target="_blank" rel="noopener noreferrer">Página pública</Button>
                {at && <Button size="small" onClick={() => setResposta(at)}>Resposta pronta</Button>}
                {at && <Button size="small" variant="outlined" color="primary" onClick={() => setAberto(at.id)}>Abrir card</Button>}
              </div>
              <div className={c.codigo} style={{ marginTop: 8 }}>{res.protocolo}</div>
            </Paper>
            <Grid container spacing={2} style={{ marginTop: 4 }}>
              <Bloco titulo="Atendimento">
                <Campo rotulo="O.S">{d.os}</Campo>
                <Campo rotulo="Serviço">{[d.tipo, d.sistema].filter(Boolean).join(" / ") || "-"}</Campo>
                <Campo rotulo="Status O.S">{at?.status_os}</Campo>
                <Campo rotulo="O.S filha">{at?.os_filha}</Campo>
              </Bloco>
              <Bloco titulo="Técnico">
                <Campo rotulo="QRA">{String(d.qra)}</Campo>
                <Campo rotulo="Nome">{d.tecnico || at?.tecnico || "não localizado"}</Campo>
              </Bloco>
              <Bloco titulo="Cliente">
                {at ? (
                  <>
                    <Campo rotulo="Conta">{at.conta}</Campo>
                    <Campo rotulo="Filial">{at.filial}</Campo>
                    <Campo rotulo="Grupo">{at.grupo}</Campo>
                  </>
                ) : <Typography variant="body2" color="textSecondary">Entre no sistema para ver o cliente.</Typography>}
              </Bloco>
              <Bloco titulo="Horários e analistas">
                <Campo rotulo="Início">{dataBR(d.inicio)}</Campo>
                <Campo rotulo="Término">{dataBR(d.fim)}</Campo>
                <Campo rotulo="Duração">{duracao(d.duracao_min)}</Campo>
                <Campo rotulo="Abertura">{at?.analista}</Campo>
                <Campo rotulo="Protocolo por">{d.analista}</Campo>
              </Bloco>
              {at?.observacao && (
                <Grid item xs={12}>
                  <Paper variant="outlined" style={{ padding: 12 }}>
                    <Typography variant="subtitle2" gutterBottom>Observação final</Typography>
                    <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>{at.observacao}</Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </>
        )}

        {res?.legado && (
          <>
            <Typography variant="subtitle1" style={{ marginTop: 16 }}>{`Protocolo antigo ${res.protocolo} · ${res.atendimentos.length} atendimento(s)`}</Typography>
            {res.atendimentos.map((a) => (
              <Paper key={a.id} variant="outlined" style={{ padding: 12, marginTop: 8, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <Typography variant="body2"><b>{`O.S ${a.os || "-"}`}</b>{` · ${[a.tipo, a.sistema].filter(Boolean).join(" / ")}`}</Typography>
                  <Typography variant="caption" color="textSecondary">
                    {`${a.filial || a.conta || "-"} · QRA ${a.qra ?? "-"} ${a.tecnico || ""} · ${dataBR(a.inicio)} → ${dataBR(a.fim)}`}
                  </Typography>
                </div>
                <Button size="small" variant="outlined" color="primary" onClick={() => setAberto(a.id)}>Abrir card</Button>
              </Paper>
            ))}
          </>
        )}
      </Paper>
      <DialogCard id={aberto} open={!!aberto} onClose={() => setAberto(null)} />
      {resposta && <DialogProtocolo card={resposta} open onClose={() => setResposta(null)} />}
    </MainContainer>
  );
};
